import { useMemo } from "react";

import {
  Badge,
  FontIcon,
  TableColumnDefinition,
  TableRowDefinition,
  TableWithDefaultFilters,
} from "@ezmid/components-library";
import { faCoins, faCreditCard } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useTranslation } from "react-i18next";

import { useGetPaymentsByUserIdQuery } from "../../../api/user-payments.generated";
import { useFormatters } from "../../../hooks";

type Props = {
  id: string;
};

type TableData = {
  id: string;
  vendorProductID: string;
  priceCurrencyCode: string;
  createdAt: string;
  status: string;
};

export function UserPaymentsCard<T extends object>({ id }: Props) {
  const { t } = useTranslation();
  const { formatDate } = useFormatters();
  const { currentData, isFetching } = useGetPaymentsByUserIdQuery(
    {
      id: id ?? "",
    },
    {
      refetchOnMountOrArgChange: true,
    }
  );
  const memoizedData: Array<TableData> = useMemo(
    () =>
      currentData?.items?.map((item) => {
        return {
          id: item?.id || "",
          status: item?.status || "",
          priceCurrencyCode: item?.priceCurrencyCode || "",
          vendorProductID: item?.vendorProductID || "",
          createdAt: item?.createdAt || "",
        };
      }) || [],
    [currentData]
  );
  const columns: Array<TableColumnDefinition<TableData>> = useMemo(
    () => [
      {
        Header: t("table.header.id"),
        Cell: ({ row }: TableRowDefinition<T>) => {
          const idValue = row?.original.id || "-";

          return (
            <div className="w-full max-w-[180px] min-w-0">
              <span
                className="block truncate whitespace-nowrap"
                title={idValue}
              >
                {idValue}
              </span>
            </div>
          );
        },
        id: "id",
        accessor: "id",
        width: "180px",
      },
      {
        Header: t("table.header.vendorProductID"),
        Cell: ({ row }: TableRowDefinition<T>) => {
          const vendorProductID =
            row?.original?.vendorProductID || t("label.free");

          return (
            <div className="w-full max-w-[240px] min-w-0">
              <span
                className="block truncate whitespace-nowrap"
                title={vendorProductID}
              >
                {vendorProductID}
              </span>
            </div>
          );
        },
        id: "vendorProductID",
        accessor: "vendorProductID",
        width: "240px",
      },
      {
        Header: t("table.header.priceCurrencyCode"),
        Cell: ({ row }: TableRowDefinition<T>) => {
          const currencyCode = row?.original.priceCurrencyCode ?? "";

          return (
            <Badge
              variant="pill"
              status="break"
              leftIcon={
                <FontIcon
                  marginTop={3}
                  customIcon={<FontAwesomeIcon size="lg" icon={faCoins} />}
                />
              }
            >
              <span title={currencyCode}>{currencyCode}</span>
            </Badge>
          );
        },
        id: "priceCurrencyCode",
        accessor: "priceCurrencyCode",
        width: "150px",
      },
      {
        Header: t("table.header.createdAt"),
        Cell: ({ row }: TableRowDefinition<T>) => {
          const createdAt = formatDate(row?.original.createdAt);

          return (
            <span className="whitespace-nowrap" title={createdAt}>
              {createdAt}
            </span>
          );
        },
        id: "createdAt",
        accessor: "createdAt",
        width: "180px",
      },
      {
        Header: t("table.header.status"),
        Cell: ({ row }: TableRowDefinition<T>) => {
          const status =
            row?.original.status === "ps-purchased"
              ? "success"
              : row?.original.status === "ps-canceled"
              ? "danger"
              : "warning";
          const statusLabel = row?.original.status.split("-")[1];

          return (
            <Badge status={status} variant="pill">
              <span title={statusLabel}>{statusLabel}</span>
            </Badge>
          );
        },
        id: "status",
        accessor: "status",
        width: "150px",
      },
    ],
    []
  );
  return (
    <TableWithDefaultFilters
      tableData={memoizedData}
      tableColumns={columns}
      showPagination={false}
      showToolbar={false}
      headerIcon={
        <FontIcon
          marginTop={6}
          customIcon={<FontAwesomeIcon icon={faCreditCard} />}
        />
      }
      headerText={t("user.payments.information")}
      isLoading={isFetching}
    />
  );
}
